import type { AdminHostAgent } from '../../types/admin';
import { formatHostLoadBytes, getHostLoadState } from '../../utils/hostLoad';

const stateLabels = {
  fresh: 'Current',
  stale: 'Stale',
  waiting: 'Waiting for metrics',
  unavailable: 'Unavailable',
} as const;

export function HostLoadDisplay({ agent, compact = false }: {
  agent: AdminHostAgent;
  compact?: boolean;
}) {
  const state = getHostLoadState(agent);
  const snapshot = agent.hostLoad;
  const stateClass = state === 'fresh'
    ? 'text-neon-400'
    : state === 'stale'
      ? 'text-yellow-300'
      : 'text-gray-500';

  if (!snapshot) {
    return (
      <div className={compact ? 'min-w-44' : 'p-4 rounded-lg bg-dark-700'}>
        <div className={`text-sm font-medium ${stateClass}`}>{stateLabels[state]}</div>
        <p className="text-xs text-gray-500 mt-1">
          {state === 'waiting'
            ? 'Socket connected; awaiting the first valid sample.'
            : 'No valid host load snapshot is available.'}
        </p>
      </div>
    );
  }

  const cpuPercent = Math.min(100, Math.max(0, snapshot.cpu.usagePercent));
  const memoryPercent = Math.min(100, Math.max(0, snapshot.memory.usagePercent));
  const observed = new Date(snapshot.observedAt);
  const observedLabel = Number.isFinite(observed.getTime()) ? observed.toLocaleString() : 'unknown time';

  return (
    <div className={compact ? 'min-w-52 space-y-2' : 'p-4 rounded-lg bg-dark-700 space-y-3'}>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className={`font-medium ${stateClass}`}>{stateLabels[state]}</span>
        <span className="text-gray-500" title={observedLabel}>{observedLabel}</span>
      </div>
      <LoadBar
        label="CPU"
        value={`${cpuPercent.toFixed(1)}% · ${snapshot.cpu.logicalCores} logical cores`}
        percent={cpuPercent}
        stale={state === 'stale'}
      />
      <LoadBar
        label="RAM"
        value={`${formatHostLoadBytes(snapshot.memory.usedBytes)} / ${formatHostLoadBytes(snapshot.memory.totalBytes)} · ${formatHostLoadBytes(snapshot.memory.availableBytes)} available · ${memoryPercent.toFixed(1)}%`}
        percent={memoryPercent}
        stale={state === 'stale'}
      />
    </div>
  );
}

function LoadBar({ label, value, percent, stale }: {
  label: string;
  value: string;
  percent: number;
  stale: boolean;
}) {
  return (
    <div>
      <div className="flex justify-between gap-3 text-xs text-gray-400">
        <span>{label}</span><span className="text-right">{value}</span>
      </div>
      <div className="progress-bar mt-1">
        <div
          className={`progress-fill ${stale ? 'opacity-40' : ''} ${percent > 90 ? 'progress-fill-danger' : ''}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
